import React from 'react';
import { Alert, Box, Button } from '@mui/material';

/**
 * Marks a session that is previewing an unreleased feature.
 *
 * <p>
 * Preview shows one person a feature nobody else can reach. Without a visible marker, a screenshot
 * taken during verification is indistinguishable from evidence that the feature shipped — which is
 * how a status update ends up claiming a release that has not happened.
 * </p>
 *
 * <p>
 * Driven by a browser-readable cookie the server sets alongside the signed one, so the 99.9% of
 * sessions that are not previewing anything cost no request. The cookie is a display hint, not a
 * control: forging it renders a banner and grants nothing, because the authoritative cookie is
 * signed, HttpOnly, and useless without the preview role.
 * </p>
 */
const MARKER_COOKIE = 'AskIAM.PreviewActive';

function readMarker(): string[] {
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${MARKER_COOKIE}=`));

  if (!match) return [];

  try {
    return decodeURIComponent(match.slice(MARKER_COOKIE.length + 1))
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

const PreviewBanner: React.FC = () => {
  const features = React.useMemo(readMarker, []);

  if (features.length === 0) return null;

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      <Alert
        severity="warning"
        variant="filled"
        action={
          <Button color="inherit" size="small" href="/preview?feature=none">
            End preview
          </Button>
        }
      >
        Previewing {features.join(', ')}. This feature is not enabled for anyone else — do not treat
        this view as evidence that it has shipped.
      </Alert>
    </Box>
  );
};

export default PreviewBanner;
